import { useCallback } from 'react';
import NiceModal from '@ebay/nice-modal-react';

import { clearOrbitSession, OrbitMutationOutcome } from 'api/orbit';

import { useOrbitContext } from './OrbitContext';
import { setOrbitPendingWorkspace } from './orbitPendingWorkspace';
import OrbitDeleteChatModal from './page/components/OrbitDeleteChatModal';

/**
 * "Delete chat?" from any chat menu, the full page's and the side panel's alike. Returns
 * `confirmDelete(sessionId, title, onOpenChatDeleted)`; the last runs once the popup has closed on a
 * deleted chat that was the open one. Every mounted list drops the chat with the DELETE itself.
 */
const useOrbitChatDelete = () => {
  const { sessionId, clearConversation } = useOrbitContext();

  // The open chat is deleted through the provider, which also drops it, so the surface falls back
  // to a new chat; any other chat is a plain DELETE.
  const deleteConversation = useCallback(
    async (deletedId: string): Promise<OrbitMutationOutcome<null>> => {
      const outcome =
        deletedId === sessionId ? await clearConversation() : await clearOrbitSession(deletedId);

      if (outcome.kind === 'ok') {
        setOrbitPendingWorkspace(deletedId, null);
      }

      return outcome;
    },
    [sessionId, clearConversation]
  );

  return useCallback(
    (deletedId: string, title: string, onOpenChatDeleted?: () => void) => {
      const wasOpen = deletedId === sessionId;
      NiceModal.show(OrbitDeleteChatModal, {
        title,
        onConfirm: () => deleteConversation(deletedId),
        onDeleted: () => {
          if (wasOpen) {
            onOpenChatDeleted?.();
          }
        }
      });
    },
    [deleteConversation, sessionId]
  );
};

export default useOrbitChatDelete;
